/**
 * The `ChangingSections` component is used to display a list of changing sections. This component cannot be used for files.
 *
 * @property version - The document version or draft to be rendered.
 * @property changedSectionIds - An array of identifiers for the sections that have changed / are selected by the user.
 * @property isEntireDocumentAsDefault - Optional flag indicating if the entire document should be used as the default option when no specific sections have changes.
 * @property onRemove - If specified a dismiss button is provided that calls this callback.
 */

import { useContext } from 'react';
import { Body1, Body1Strong, Link as FluentLink } from '@fluentui/react-components';

import { SelectList, TSelectElement } from '@shared/components';
import {
  DOCUMENT_HTML_ATTRIBUTES,
  DocumentContext,
  TDocument,
  TDraft,
  THeading,
  TVersion,
  useScrollContent,
} from '@modules/documents';
import { FileIcon, useItemScreenStore } from '@modules/items';
import useImpactSourceStore from '../stores/useImpactSourceStore';

const getSelectListElements = (
  doc: TDocument,
  version: TVersion | TDraft,
  changedSectionIds: string[],
  autoDisabledIds: ReadonlySet<string>,
  scrollToHeading: (heading: THeading) => void,
  isEntireDocumentAsDefault?: boolean,
  onRemove?: (id: string) => void
): TSelectElement[] => {
  if (changedSectionIds.length <= 0) {
    if (isEntireDocumentAsDefault)
      return [
        {
          content: `Entire document`,
          icon: (
            <FileIcon
              type="DOCUMENT"
              path={doc.resolvedPath.map((pathItem) => pathItem._id)}
              size="small"
              isBlock={doc.isBlock}
              isFrozen={doc.isFrozen}
              isTemplate={doc.isTemplate}
              name={doc.name}
            />
          ),
        },
      ];
    return [
      {
        content: 'No changes within sections found.',
      },
    ];
  }

  const impactedHeadings = version.headings.filter((h) => changedSectionIds.includes(h.id));

  return impactedHeadings.map((h) => {
    const isAutoDisabled = autoDisabledIds.has(h.id);
    const headingText = `${h.numbering} ${h.text}`;

    return {
      content: (
        <FluentLink onClick={() => scrollToHeading(h)} appearance="subtle" aria-label="scroll to heading">
          {isAutoDisabled ? <Body1>{headingText}</Body1> : <Body1Strong>{headingText}</Body1Strong>}
        </FluentLink>
      ),
      // Hide the dismiss (remove) button if a heading is auto-disabled (i.e. selected by the system because an ancestor is selected)
      onRemove: !isAutoDisabled && onRemove ? () => onRemove(h.id) : undefined,
    };
  });
};

type TProps = {
  version: TVersion | TDraft;
  changedSectionIds: string[];
  isEntireDocumentAsDefault?: boolean;
  onRemove?: (id: string) => void;
};

const ChangingSections = ({ version, changedSectionIds, isEntireDocumentAsDefault, onRemove }: TProps) => {
  const { doc } = useContext(DocumentContext);
  const scrollContent = useScrollContent();
  const { autoDisabledIds } = useImpactSourceStore();

  const handleScrollToHeading = (heading: THeading) => {
    scrollContent({ value: heading.id });
  };

  // useMemo makes no sense here, as the elements are always re-rendered:
  const selectListElements = getSelectListElements(
    doc,
    version,
    changedSectionIds,
    autoDisabledIds,
    handleScrollToHeading,
    isEntireDocumentAsDefault,
    onRemove
  );

  return <SelectList elements={selectListElements} />;
};

export default ChangingSections;
